import React, { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  TrashIcon,
  EyeIcon,
  ClockIcon,
  TvIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Table from "../../components/common/Table";
import ScheduleDetailsModal from "../../components/modals/ScheduleDetailsModal";
import { useSchedular } from "../../hooks/useSchedular";
import { useAllSchedulesInfinite } from "../../hooks/useAllSchedulesInfinite";
import { useScreenOptions } from "../../hooks/useScreenOptions";
import { usePlaylistOptions } from "../../hooks/usePlaylistOptions";
import {
  ScheduleBatchItem,
  ScheduleStatusFilter,
} from "../../schemas/scheduleSchema";

interface DropdownOption {
  value: string;
  label: string;
}

const STATUS_OPTIONS: { label: string; value: ScheduleStatusFilter | "" }[] = [
  { label: "All", value: "" },
  { label: "Active", value: "ACTIVE" },
  { label: "Upcoming", value: "UPCOMING" },
  { label: "Expired", value: "EXPIRED" },
];

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const formatTime = (time?: string) => {
  if (!time) return "All day";
  const parts = time.split(":");
  const hours = parseInt(parts[0]);
  const minutes = parts[1] || "00";
  const seconds = parts[2] || "00";
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes}:${seconds} ${ampm}`;
};

const Schedules: React.FC = () => {
  const { deleteScheduleBatch, deleteScheduleBatchForScreens } = useSchedular();

  const [statusFilter, setStatusFilter] = useState<ScheduleStatusFilter | "">(
    "",
  );
  const [screenFilter, setScreenFilter] = useState<string>("");
  const [playlistFilter, setPlaylistFilter] = useState<string>("");

  const { screenOptions } = useScreenOptions();
  const { playlistOptions } = usePlaylistOptions();

  const {
    schedules,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useAllSchedulesInfinite({
    status: statusFilter || undefined,
    screenId: screenFilter || undefined,
    playlistId: playlistFilter || undefined,
  });

  const [viewSchedule, setViewSchedule] = useState<ScheduleBatchItem | null>(
    null,
  );
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    batchId: string;
    playlistName: string;
    screenIds: string[];
    mode: "entire" | "selected";
  }>({
    isOpen: false,
    batchId: "",
    playlistName: "",
    screenIds: [],
    mode: "entire",
  });

  const handleView = (schedule: ScheduleBatchItem) => {
    setViewSchedule(schedule);
  };

  const handleDelete = (schedule: ScheduleBatchItem) => {
    setDeleteConfirm({
      isOpen: true,
      batchId: schedule.batchId,
      playlistName: schedule.playlistName,
      screenIds: [],
      mode: "entire",
    });
  };

  const handleDeleteSelectedScreens = (
    schedule: ScheduleBatchItem,
    screenIds: string[],
  ) => {
    setDeleteConfirm({
      isOpen: true,
      batchId: schedule.batchId,
      playlistName: schedule.playlistName,
      screenIds,
      mode: "selected",
    });
  };

  const confirmDelete = () => {
    if (deleteConfirm.mode === "selected") {
      deleteScheduleBatchForScreens({
        batchId: deleteConfirm.batchId,
        screenIds: deleteConfirm.screenIds,
      });
    } else {
      deleteScheduleBatch(deleteConfirm.batchId);
    }
    setDeleteConfirm({
      isOpen: false,
      batchId: "",
      playlistName: "",
      screenIds: [],
      mode: "entire",
    });
  };

  const columns: ColumnDef<ScheduleBatchItem>[] = [
    {
      accessorKey: "playlistName",
      header: "Playlist",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-800">
            {row.original.playlistName}
          </span>
          {row.original.isOverride && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
              Override
            </span>
          )}
        </div>
      ),
    },
    {
      id: "dateRange",
      header: "Date Range",
      accessorFn: (row) => row.startDate,
      cell: ({ row }) => (
        <span className="text-sm text-gray-600">
          {formatDate(row.original.startDate)} —{" "}
          {formatDate(row.original.endDate)}
        </span>
      ),
    },
    {
      id: "timeSlot",
      header: "Time Slot",
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <ClockIcon className="w-4 h-4 text-gray-400" />
          {row.original.startTime
            ? `${formatTime(row.original.startTime)} - ${formatTime(row.original.endTime)}`
            : "All day"}
        </div>
      ),
    },
    {
      id: "daysOfWeek",
      header: "Days",
      cell: ({ row }) => {
        const days = row.original.daysOfWeek;
        if (!days || days.length === 0) {
          return <span className="text-sm text-gray-400">All days</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {days.map((day) => (
              <span
                key={day}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700"
              >
                {day.slice(0, 3)}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      id: "screens",
      header: "Screens",
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1 text-sm text-gray-600">
          <TvIcon className="w-4 h-4" />
          {row.original.screens.length}
        </span>
      ),
    },
    {
      id: "createdAt",
      header: "Created",
      accessorFn: (row) => row.createdAt,
      cell: ({ row }) => (
        <span className="text-sm text-gray-500">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    },
  ];

  const actions = [
    {
      label: "View",
      icon: <EyeIcon className="h-4 w-4" />,
      onClick: handleView,
      variant: "primary" as const,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
    },
  ];

  return (
    <div className="flex flex-col px-6 py-2">
      <div className="flex justify-between items-center mb-2 gap-3">
        <h1 className="text-2xl font-bold text-gray-800">Schedules</h1>
        <div className="flex items-center gap-2">
          <select
            value={screenFilter}
            onChange={(e) => setScreenFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Screens</option>
            {(screenOptions as DropdownOption[]).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={playlistFilter}
            onChange={(e) => setPlaylistFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Playlists</option>
            {(playlistOptions as DropdownOption[]).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as ScheduleStatusFilter | "")
            }
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Table
        data={(schedules || []) as ScheduleBatchItem[]}
        columns={columns}
        actions={actions}
        searchable={false}
        pagination={true}
        isLoading={isLoading}
        maxHeight="67vh"
        getRowId={(item) => item.batchId}
        infinite
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
      />

      {/* Schedule Details Modal */}
      {viewSchedule && (
        <ScheduleDetailsModal
          isOpen={!!viewSchedule}
          schedule={viewSchedule}
          onClose={() => setViewSchedule(null)}
          onDelete={handleDelete}
          onDeleteSelectedScreens={handleDeleteSelectedScreens}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 relative">
            <button
              onClick={() =>
                setDeleteConfirm({
                  isOpen: false,
                  batchId: "",
                  playlistName: "",
                  screenIds: [],
                  mode: "entire",
                })
              }
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <div className="text-center mb-5">
              <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-800">
                {deleteConfirm.mode === "selected"
                  ? "Delete Schedule from Selected Screens"
                  : "Delete Entire Schedule"}
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                {deleteConfirm.mode === "selected" ? (
                  <>
                    This will permanently delete the schedule{" "}
                    <span className="font-medium text-gray-700">
                      &quot;{deleteConfirm.playlistName}&quot;
                    </span>{" "}
                    from{" "}
                    <strong>
                      {deleteConfirm.screenIds.length} selected screen
                      {deleteConfirm.screenIds.length === 1 ? "" : "s"}
                    </strong>
                    . This action cannot be undone.
                  </>
                ) : (
                  <>
                    This will permanently delete the schedule{" "}
                    <span className="font-medium text-gray-700">
                      &quot;{deleteConfirm.playlistName}&quot;
                    </span>{" "}
                    from <strong>all screens</strong>. This action cannot be
                    undone.
                  </>
                )}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() =>
                  setDeleteConfirm({
                    isOpen: false,
                    batchId: "",
                    playlistName: "",
                    screenIds: [],
                    mode: "entire",
                  })
                }
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedules;
